
import { getCommonPrintStyles, getHeaderHtml, getFooterHtml, getSignatureHtml } from "./printStyles";
import { downloadAsJPG, downloadAsPDF, createPrintDocument } from "./printingBridge";
import { toast } from "sonner";

const NEW_STUDENTS_PER_PAGE = 15;

interface NewStudentData {
  id: string;
  name: string;
  fathersName: string;
  program: string;
  enrollmentDate: string;
}

/**
 * Export new students list as JPG with pagination support
 */
export const printNewStudentsListPaginatedAsJPG = async (
  newStudents: NewStudentData[],
  startPage: number = 1,
  endPage: number = 1
) => {
  try {
    console.log('Starting new students list JPG download...', { newStudents: newStudents.length, startPage, endPage });
    
    // Show loading toast
    const loadingToast = toast.loading('Generating new students list JPG...');
    
    const { content, filename } = generateNewStudentsContent(newStudents, startPage, endPage);
    
    console.log('Starting JPG download with filename:', filename);
    
    // Download as JPG directly
    await downloadAsJPG(content, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download new students report');
      }
    });
    
    // Dismiss loading toast and show success
    toast.dismiss(loadingToast);
    const pageText = startPage === endPage ? `page ${startPage}` : `pages ${startPage}-${endPage}`;
    toast.success(`New students report ${pageText} downloaded as JPG!`);
    
  } catch (error) {
    console.error('Error downloading paginated new students report:', error);
    toast.error('Failed to download new students report');
  }
};

/**
 * Export new students list as PDF with pagination support
 */
export const printNewStudentsListPaginatedAsPDF = async (
  newStudents: NewStudentData[],
  startPage: number = 1,
  endPage: number = 1
) => {
  try {
    console.log('Starting new students list PDF download...', { newStudents: newStudents.length, startPage, endPage });
    
    // Show loading toast
    const loadingToast = toast.loading('Generating new students list PDF...');
    
    const { content, filename } = generateNewStudentsContent(newStudents, startPage, endPage);
    
    console.log('Starting PDF download with filename:', filename);
    
    // Download as PDF
    await downloadAsPDF(content, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download new students report');
      }
    });
    
    // Dismiss loading toast and show success
    toast.dismiss(loadingToast);
    const pageText = startPage === endPage ? `page ${startPage}` : `pages ${startPage}-${endPage}`;
    toast.success(`New students report ${pageText} downloaded as PDF!`);
    
  } catch (error) {
    console.error('Error downloading paginated new students report:', error);
    toast.error('Failed to download new students report');
  }
};

const generateNewStudentsContent = (newStudents: NewStudentData[], startPage: number, endPage: number) => {
  const totalPages = Math.ceil(newStudents.length / NEW_STUDENTS_PER_PAGE);
  const actualEndPage = Math.min(endPage, totalPages);
  const actualStartPage = Math.max(1, startPage);
  
  console.log('Calculated pages:', { totalPages, actualStartPage, actualEndPage });
  
  // Calculate which students to include
  const startIndex = (actualStartPage - 1) * NEW_STUDENTS_PER_PAGE;
  const endIndex = actualEndPage * NEW_STUDENTS_PER_PAGE;
  const studentsToShow = newStudents.slice(startIndex, endIndex);
  
  console.log('Students to show:', { startIndex, endIndex, count: studentsToShow.length });
  
  // Group students by pages
  const pages: NewStudentData[][] = [];
  for (let i = 0; i < studentsToShow.length; i += NEW_STUDENTS_PER_PAGE) {
    pages.push(studentsToShow.slice(i, i + NEW_STUDENTS_PER_PAGE));
  }
  
  // Generate content for all pages
  const allPagesContent = pages.map((pageStudents, pageIndex) => {
    const currentPageNumber = actualStartPage + pageIndex;
    
    return `
      <div class="page-content" style="page-break-after: ${pageIndex < pages.length - 1 ? 'always' : 'auto'}; min-height: 1050px; display: flex; flex-direction: column;">
        ${getHeaderHtml(`New Students Report - Page ${currentPageNumber} of ${totalPages}`)}
        
        <div class="page-info">
          <strong>Page ${currentPageNumber} of ${totalPages}</strong> | 
          Students ${(currentPageNumber - 1) * NEW_STUDENTS_PER_PAGE + 1} - ${Math.min(currentPageNumber * NEW_STUDENTS_PER_PAGE, newStudents.length)} of ${newStudents.length}
        </div>
        
        <div class="table-container" style="flex: 1; margin-bottom: 20px;">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Student Name</th>
                <th>Father's Name</th>
                <th>Program</th>
                <th>Enrollment Date</th>
              </tr>
            </thead>
            <tbody>
              ${pageStudents.map((student, index) => {
                const displayId = (startIndex + (pageIndex * NEW_STUDENTS_PER_PAGE) + index + 1).toString();
                const formattedDate = new Date(student.enrollmentDate).toLocaleDateString();
                
                return `
                  <tr>
                    <td>${displayId}</td>
                    <td>${student.name || 'N/A'}</td>
                    <td>${student.fathersName || 'N/A'}</td>
                    <td>${student.program || 'No Program'}</td>
                    <td>${formattedDate}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="page-footer" style="margin-top: auto;">
          ${getSignatureHtml('report')}
          ${getFooterHtml('report')}
        </div>
      </div>
    `;
  }).join('');

  const styles = `
    ${getCommonPrintStyles()}
    .page-content {
      min-height: 1050px;
      position: relative;
      display: flex;
      flex-direction: column;
      margin-bottom: 20px;
      page-break-inside: avoid;
    }
    .page-info {
      text-align: center;
      font-size: 0.9em;
      color: #666;
      margin: 10px 0;
      padding: 8px;
      background: #f8f9fa;
      border-radius: 4px;
    }
    .table-container {
      flex: 1;
      margin-bottom: 20px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 16px;
      font-size: 0.85em;
    }
    th, td {
      padding: 8px 6px;
      border-bottom: 1px solid #ddd;
      text-align: left;
      vertical-align: top;
    }
    th {
      background: #f5f5f5;
      font-weight: bold;
      border-bottom: 2px solid #ccc;
      font-size: 0.9em;
    }
    .page-footer {
      margin-top: auto;
      page-break-inside: avoid;
      position: relative;
      min-height: 120px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
    }
    .signatures {
      margin-top: 30px;
      margin-bottom: 15px;
      flex-shrink: 0;
    }
    .footer {
      margin-top: 15px;
      flex-shrink: 0;
      position: relative;
    }
    @page {
      size: A4;
      margin: 0.5in;
    }
    @media print {
      .page-content {
        page-break-after: always;
        min-height: 1050px;
      }
      .page-content:last-child {
        page-break-after: auto;
      }
    }
  `;

  // Generate the complete print document
  const printDocument = createPrintDocument(allPagesContent, 'New_Students_Report_Paginated', styles);
  
  // Create filename
  const filename = actualStartPage === actualEndPage 
    ? `New_Students_Report_Page_${actualStartPage}_${new Date().toISOString().split('T')[0]}`
    : `New_Students_Report_Pages_${actualStartPage}-${actualEndPage}_${new Date().toISOString().split('T')[0]}`;
  
  return { content: printDocument, filename };
};
